import React, { useState } from "react";
import { X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const labelClass =
  "mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500";

const selectClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const initialState = {
  name: "",
  legal_name: "",
  headquarters_city: "",
  headquarters_country: "Portugal",
  official_url: "",
  entity_type: "organisation",
  priority: "B",
  profile_type: "family_office",
  sector: "hotels_resorts",
  strategy: "acquisition",
  investment_geography: "Portugal",
  description: "",
  why_relevant: "",
  contact_name: "",
  contact_job_title: "",
  contact_email: "",
  contact_phone: "",
  contact_verification_status: "unverified",
  contact_source_url: "",
};

export default function InvestorOwnershipForm({
  meta,
  saving,
  onCancel,
  onSave,
  t,
}) {
  const [form, setForm] = useState(initialState);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = (event) => {
    event.preventDefault();

    onSave({
      name: form.name.trim(),
      legal_name: form.legal_name.trim(),
      headquarters_city: form.headquarters_city.trim(),
      headquarters_country: form.headquarters_country.trim(),
      countries_of_operation: form.headquarters_country
        ? [form.headquarters_country]
        : [],
      investment_geographies: form.investment_geography.trim()
        ? [form.investment_geography.trim()]
        : [],
      official_url: form.official_url.trim(),
      priority: form.priority,
      evidence_status: "unverified",
      confidence_level: "low",
      lawful_basis_status: "not_assessed",
      outreach_status: "not_reviewed",
      relationship_stage: "under_review",
      entity_type: form.entity_type,
      profile_types: [form.profile_type],
      investment_sectors: [form.sector],
      strategies: [form.strategy],
      description: form.description.trim(),
      why_relevant: form.why_relevant.trim(),
      contacts: [
        {
          name: form.contact_name.trim(),
          job_title: form.contact_job_title.trim(),
          email: form.contact_email.trim(),
          phone: form.contact_phone.trim(),
          source_url: (
            form.contact_source_url || form.official_url
          ).trim(),
          verification_status:
            form.contact_verification_status,
          is_primary: true,
        },
      ],
      sources: [
        {
          url: (
            form.contact_source_url || form.official_url
          ).trim(),
          title: "Official company source",
          source_type: "official_website",
          confidence_level: "low",
        },
      ],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-8">
      <form
        onSubmit={submit}
        className="w-full max-w-5xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#A77E35]">
              {t(
                "investorOwnership.form.eyebrow",
                "Verified partner profile"
              )}
            </p>
            <h2 className="mt-1 font-serif text-3xl text-[#0B2545]">
              {t(
                "investorOwnership.form.title",
                "Add investor or organisation"
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-[#0B2545]"
            aria-label={t("common.close", "Close")}
          >
            <X size={22} />
          </button>
        </div>

        <div className="grid gap-8 p-6 lg:grid-cols-2">
          <section className="space-y-5">
            <h3 className="border-b border-slate-100 pb-3 font-serif text-xl text-[#0B2545]">
              {t(
                "investorOwnership.form.companyInformation",
                "Investor or organisation information"
              )}
            </h3>

            <div>
              <label className={labelClass}>
                {t("investorOwnership.form.name", "Investor or organisation name")} *
              </label>
              <Input
                required
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.legalName", "Legal name")}
                </label>
                <Input
                  value={form.legal_name}
                  onChange={(event) =>
                    update("legal_name", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.entityType", "Entity type")} *
                </label>
                <select
                  required
                  value={form.entity_type}
                  onChange={(event) =>
                    update("entity_type", event.target.value)
                  }
                  className={selectClass}
                >
                  <option value="organisation">
                    {t("investorOwnership.form.organisation", "Organisation")}
                  </option>
                  <option value="person">
                    {t("investorOwnership.form.person", "Public professional")}
                  </option>
                </select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.country", "Country")} *
                </label>
                <Input
                  required
                  value={form.headquarters_country}
                  onChange={(event) =>
                    update("headquarters_country", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.city", "City")}
                </label>
                <Input
                  value={form.headquarters_city}
                  onChange={(event) =>
                    update("headquarters_city", event.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t("investorOwnership.form.website", "Official website")} *
              </label>
              <Input
                required
                type="url"
                placeholder="https://"
                value={form.official_url}
                onChange={(event) =>
                  update("official_url", event.target.value)
                }
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.role", "Investor profile type")} *
                </label>
                <select
                  value={form.profile_type}
                  onChange={(event) =>
                    update("profile_type", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.profile_types || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.sector", "Investment sector")} *
                </label>
                <select
                  value={form.sector}
                  onChange={(event) =>
                    update("sector", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.investment_sectors || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.strategy", "Investment strategy")} *
                </label>
                <select
                  value={form.strategy}
                  onChange={(event) =>
                    update("strategy", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.strategies || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t("investorOwnership.form.description", "Description")}
              </label>
              <textarea
                rows={4}
                value={form.description}
                onChange={(event) =>
                  update("description", event.target.value)
                }
                className="w-full border border-slate-200 p-3 text-sm outline-none focus:border-[#C5A059]"
              />
            </div>
          </section>

          <section className="space-y-5">
            <h3 className="border-b border-slate-100 pb-3 font-serif text-xl text-[#0B2545]">
              {t(
                "investorOwnership.form.professionalContact",
                "Professional contact"
              )}
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.contactName", "Contact name or department")} *
                </label>
                <Input
                  required
                  value={form.contact_name}
                  onChange={(event) =>
                    update("contact_name", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.jobTitle", "Position")}
                </label>
                <Input
                  value={form.contact_job_title}
                  onChange={(event) =>
                    update("contact_job_title", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.email", "Email")} *
                </label>
                <Input
                  required
                  type="email"
                  value={form.contact_email}
                  onChange={(event) =>
                    update("contact_email", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.phone", "Telephone")} *
                </label>
                <Input
                  required
                  type="tel"
                  value={form.contact_phone}
                  onChange={(event) =>
                    update("contact_phone", event.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t("investorOwnership.form.contactSource", "Contact source URL")} *
              </label>
              <Input
                required
                type="url"
                placeholder="https://"
                value={form.contact_source_url}
                onChange={(event) =>
                  update("contact_source_url", event.target.value)
                }
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.priority", "Priority")}
                </label>
                <select
                  value={form.priority}
                  onChange={(event) =>
                    update("priority", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.priorities || []).map((item) => (
                    <option key={item} value={item}>
                      Priority {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t("investorOwnership.form.contactVerification", "Contact verification")}
                </label>
                <select
                  value={form.contact_verification_status}
                  onChange={(event) =>
                    update(
                      "contact_verification_status",
                      event.target.value
                    )
                  }
                  className={selectClass}
                >
                  {(meta?.contact_statuses || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t(
                  "investorOwnership.form.investmentGeography",
                  "Target investment geography"
                )}
              </label>
              <Input
                required
                value={form.investment_geography}
                onChange={(event) =>
                  update("investment_geography", event.target.value)
                }
                placeholder="Portugal, Iberia, Europe…"
              />
            </div>

            <div>
              <label className={labelClass}>
                {t(
                  "investorOwnership.form.relevance",
                  "Why this company is relevant"
                )}
              </label>
              <textarea
                rows={5}
                value={form.why_relevant}
                onChange={(event) =>
                  update("why_relevant", event.target.value)
                }
                className="w-full border border-slate-200 p-3 text-sm outline-none focus:border-[#C5A059]"
              />
            </div>
          </section>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            className="rounded-none"
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="rounded-none bg-[#0B2545] hover:bg-[#12345e]"
          >
            {saving
              ? t("investorOwnership.form.saving", "Saving…")
              : t("investorOwnership.form.save", "Save intelligence profile")}
          </Button>
        </div>
      </form>
    </div>
  );
}
